import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ChevronRight, Plus, RefreshCw } from "lucide-react";
import { Source } from '../lib/types';
import { formatDateTime } from '../lib/text';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
import SourceLogo from '../components/SourceLogo';

export default function DataSourcesPage() {
    const { authFetch } = useAuth();
    const navigate = useNavigate();
    const [sources, setSources] = useState<Source[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadSources();
    }, []);

    const loadSources = async () => {
        try {
            const response = await authFetch('/sources');
            if (!response.ok) throw new Error('Failed to load sources');
            const data = await response.json();
            if (data.length === 0) {
                navigate('/sources/add?redirect=%2Fsources', { replace: true });
                return;
            }
            setSources(data);
            setLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load sources');
            setLoading(false);
        }
    };

    if (loading) return <PageLoader label="Loading sources..." />;
    if (error) return <PageError title="Failed to load sources" message={error} />;

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey flex flex-wrap items-end justify-between gap-6">
                <div>
                    <h3 className="text-lg font-semibold text-graphite">Connections</h3>
                    <p className="text-sm text-coolslate mt-1">The systems and document stores your company record is built from</p>
                </div>
                <button
                    onClick={() => navigate('/sources/add?redirect=%2Fsources')}
                    className="px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Data Source
                </button>
            </div>

            <div className="grid gap-4">
                {sources.map((source) => (
                    <div
                        key={source.id}
                        className="bg-white rounded-lg shadow-sm border border-softgrey p-6 cursor-pointer hover:bg-offwhite transition-colors"
                        onClick={() => navigate(`/sources/${source.id}`)}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4 flex-1">
                                <div className="navaris-tile-tint w-12 h-12 rounded-xl border border-softgrey p-2 flex items-center justify-center">
                                    <SourceLogo type={source.source_type} size="xl" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <h4 className="font-semibold text-graphite">{source.source}</h4>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
                                        <div className="flex items-center gap-2">
                                            <Activity className="w-4 h-4 text-coolslate/70" />
                                            <span className="text-sm text-coolslate">
                                                Updated: {formatDateTime(source.updated_at)}
                                            </span>
                                        </div>
                                        {source.sync_schedule !== 'off' && source.next_sync_at && (
                                            <div className="flex items-center gap-2" title="Background sync schedule">
                                                <RefreshCw className="w-4 h-4 text-coolslate/70" />
                                                <span className="text-sm text-coolslate capitalize">
                                                    {source.sync_schedule} · Next: {formatDateTime(source.next_sync_at, false)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-coolslate/70" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
